// AboutUs.jsx
import {
  FiAward,
  FiEye,
  FiGlobe,
  FiHeart,
  FiShield,
  FiStar,
  FiTarget,
  FiUsers,
  FiCheckCircle,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";

const AboutUs = () => {
  const { t } = useTranslation();

  const objectives = [
    t("aboutPage.objectives.emergency"),
    t("aboutPage.objectives.unity"),
    t("aboutPage.objectives.wash"),
    t("aboutPage.objectives.health"),
    t("aboutPage.objectives.partnerships"),
    t("aboutPage.objectives.empowerment"),
    t("aboutPage.objectives.climate"),
    t("aboutPage.objectives.poverty"),
    t("aboutPage.objectives.food"),
    t("aboutPage.objectives.gender"),
    t("aboutPage.objectives.rights"),
    t("aboutPage.objectives.shelter"),
  ];

  const values = [
    {
      icon: FiHeart,
      title: t("aboutPage.values.empowerment.title"),
      description: t("aboutPage.values.empowerment.description"),
    },
    {
      icon: FiUsers,
      title: t("aboutPage.values.collaboration.title"),
      description: t("aboutPage.values.collaboration.description"),
    },
    {
      icon: FiShield,
      title: t("aboutPage.values.integrity.title"),
      description: t("aboutPage.values.integrity.description"),
    },
    {
      icon: FiStar,
      title: t("aboutPage.values.womenFocused.title"),
      description: t("aboutPage.values.womenFocused.description"),
    },
    {
      icon: FiEye,
      title: t("aboutPage.values.transparency.title"),
      description: t("aboutPage.values.transparency.description"),
    },
    {
      icon: FiGlobe,
      title: t("aboutPage.values.environmental.title"),
      description: t("aboutPage.values.environmental.description"),
    },
    {
      icon: FiAward,
      title: t("aboutPage.values.accountability.title"),
      description: t("aboutPage.values.accountability.description"),
    },
    {
      icon: FiTarget,
      title: t("aboutPage.values.approachability.title"),
      description: t("aboutPage.values.approachability.description"),
    },
  ];

  return (
    <main className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-800 via-green-700 to-green-800 py-24 md:py-32 text-center overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"></div>
        </div>
        <div className="relative z-10 mx-auto max-w-4xl px-4">
          <h1 className="mb-6 text-5xl md:text-6xl lg:text-7xl font-bold text-white">
            {t("aboutPage.hero.title")}
          </h1>
          <p className="text-xl md:text-2xl font-medium text-green-100">
            {t("aboutPage.hero.subtitle")}
          </p>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="px-4 py-20 md:px-8 lg:px-16">
        <div className="mx-auto max-w-7xl">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-4xl md:text-5xl font-bold text-green-800">
              {t("aboutPage.story.title")}
            </h2>
            <div className="mx-auto h-1 w-24 bg-green-600"></div>
          </div>

          <div className="mb-16 grid items-center gap-12 lg:grid-cols-2">
            <div className="space-y-6 order-2 lg:order-1">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <div className="space-y-6">
                  <p className="text-lg leading-relaxed text-gray-700">
                    {t("aboutPage.story.description1")}
                  </p>
                  <p className="text-lg leading-relaxed text-gray-700">
                    {t("aboutPage.story.description2")}
                  </p>
                  <p className="text-lg leading-relaxed text-gray-700">
                    {t("aboutPage.story.description3")}
                  </p>
                  <div className="rounded-xl border-l-4 border-green-600 bg-gradient-to-r from-green-50 to-green-100 p-6 mt-6">
                    <p className="text-lg text-gray-700 italic font-medium">
                      "{t("aboutPage.story.motto")}"
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative order-1 lg:order-2">
              <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                <img
                  src="/images/image_1.jpg"
                  alt="ACDHA Team"
                  className="h-96 w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-green-800/70 via-green-800/30 to-transparent"></div>
              </div>
              <div className="absolute -bottom-6 -right-6 rounded-xl bg-gradient-to-r from-green-600 to-green-700 p-6 text-white shadow-xl border-4 border-white">
                <div className="text-3xl font-bold mb-1">2021</div>
                <div className="text-sm font-medium opacity-90">
                  {t("aboutPage.founded")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Objectives Section */}
      <section className="bg-gradient-to-br from-green-50 to-white px-4 py-20 md:px-8 lg:px-16">
        <div className="mx-auto max-w-7xl">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-4xl md:text-5xl font-bold text-green-800">
              {t("aboutPage.objectives.title")}
            </h2>
            <div className="mx-auto h-1 w-24 bg-green-600"></div>
            <p className="mx-auto mt-6 max-w-3xl text-xl text-gray-700">
              {t("aboutPage.objectives.subtitle")}
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {objectives.map((objective, index) => (
              <div
                key={index}
                className="group rounded-xl border border-gray-200 bg-white p-6 shadow-md transition-all duration-300 hover:shadow-xl hover:border-green-300"
              >
                <div className="flex items-start space-x-4">
                  <div className="mt-1 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-green-500 to-green-600">
                    <FiCheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <p className="leading-relaxed text-gray-700 font-medium">
                    {objective}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="bg-white px-4 py-20 md:px-8 lg:px-16">
        <div className="mx-auto max-w-7xl">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-4xl md:text-5xl font-bold text-green-800">
              {t("aboutPage.values.title")}
            </h2>
            <div className="mx-auto h-1 w-24 bg-green-600"></div>
            <p className="mx-auto mt-6 max-w-3xl text-xl text-gray-700">
              {t("aboutPage.values.subtitle")}
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {values.map((value, index) => (
              <div
                key={index}
                className="group rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-green-50 p-8 text-center shadow-md transition-all duration-300 hover:shadow-xl hover:border-green-300"
              >
                <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-r from-green-500 to-green-600 shadow-lg">
                  <value.icon className="h-10 w-10 text-white" />
                </div>
                <h3 className="mb-3 text-xl font-bold text-gray-800 group-hover:text-green-700 transition-colors">
                  {value.title}
                </h3>
                <p className="text-sm leading-relaxed text-gray-600">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="bg-gradient-to-br from-green-800 via-green-700 to-green-800 px-4 py-20 md:px-8 lg:px-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"></div>
        </div>
        <div className="relative z-10 mx-auto max-w-4xl text-center">
          <h2 className="mb-6 text-4xl md:text-5xl font-bold text-white">
            {t("aboutPage.cta.title")}
          </h2>
          <p className="mx-auto mb-8 max-w-2xl text-xl text-green-100">
            {t("aboutPage.cta.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="rounded-xl bg-yellow-500 px-8 py-4 text-lg font-semibold text-green-800 shadow-lg transition-all duration-300 hover:bg-yellow-400 hover:shadow-xl">
              {t("aboutPage.cta.getInvolved")}
            </button>
            <button className="rounded-xl border-2 border-white bg-transparent px-8 py-4 text-lg font-semibold text-white transition-all duration-300 hover:bg-white/10 hover:border-green-200">
              {t("aboutPage.cta.learnMore")}
            </button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutUs;
